import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SingleSubscriberProduct } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { translate } from 'components/Translation';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import AccordionContent from '../../../common/AccordionContent';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import { finalize } from 'rxjs/operators';
import { SinglePackage } from 'utils/api/models/numbers/singlePackage';
import { getStatus, hasRequiredProducts, isEnabled, isPackageAllowed } from 'utils/packages/packageState';
import { getProductId } from 'views/Numbers/Details/Services/helpers/getProductId';
import idConfig from 'utils/ecommerce/services/id.config';
import ActivateMobileInternetDialog from './subcomponents/ActivateMobileInternetDialog';
import apiConfig from 'utils/api/config';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/mobileInternetPlansContentWrapperStyle';
import { makeStyles } from '@material-ui/core';
import { getLanguage } from 'utils/language/language';
import { SERVICE_STATUS } from 'views/Numbers/Details/Services/constants';

type dialog = 'topUpDialog' | 'changePlanDialog';

// @ts-ignore
const useStyles = makeStyles(styles);

const ACTIVE_SUBSCRIBER_PRODUCT = 'Active';

const cannotBeTurnedOff = [idConfig.PAY_AS_YOU_GO.legacyProductId, idConfig.BAQATI_PAY_AS_YOU_GO.legacyProductId];

interface MobileInternetPlansContentWrapperProps {
	subscriberProducts: SingleSubscriberProduct[];
	updateServices: types.services.updateServicesFunction;
	defaultPayG: { id: string; place: string };
	setIsMobileInternetPlansActive: React.Dispatch<React.SetStateAction<boolean>>;
}

const MobileInternetPlansContentWrapper = ({
	subscriberProducts,
	updateServices,
	defaultPayG,
	setIsMobileInternetPlansActive,
}: MobileInternetPlansContentWrapperProps) => {
	const [planPackages, setPlanPackages] = useState<SinglePackage[]>([]);
	const [topUpPackages, setTopUpPackages] = useState<SinglePackage[]>([]);
	const [loading, setLoading] = useState<{ plans: boolean; topUps: boolean }>({
		plans: false,
		topUps: false,
	});
	const [dialogType, setDialogType] = useState<dialog | null>(null);
	const classes = useStyles();

	const language = getLanguage();

	const showTopUpDialog = () => {
		setDialogType('topUpDialog');
	};

	const showChangePlanDialog = () => {
		setDialogType('changePlanDialog');
	};

	const hideDialog = () => {
		setDialogType(null);
	};

	const turnOff = useCallback(
		(singlePackage: SinglePackage, action: types.services.actionType) => {
			// add default package
			const orderItems: types.services.IOrderItem[] = [
				{
					action: 'ADD',
					ebuConfigProduct: singlePackage.ebuConfigProduct,
					...defaultPayG,
				},
			];
			updateServices(orderItems, singlePackage.name, action, true);
		},
		[updateServices, defaultPayG]
	);

	const changePlan = useCallback(() => {
		showChangePlanDialog();
	}, []);

	const activateTopUp = useCallback(() => {
		showTopUpDialog();
	}, []);

	useEffect(() => {
		setLoading((loading) => ({ ...loading, plans: true }));
		const subscription = ECommerceService.getPackages('MOBILE_INTERNET_PLANS', 'POSTPAID')
			.pipe(finalize(() => setLoading((loading) => ({ ...loading, plans: false }))))
			.subscribe(({ packages }) => {
				setPlanPackages(packages);
			});
		return () => {
			subscription.unsubscribe();
		};
	}, []);

	useEffect(() => {
		setLoading((loading) => ({ ...loading, topUps: true }));
		const subscription = ECommerceService.getPackages('MOBILE_INTERNET_PLAN_TOP_UPS', 'POSTPAID')
			.pipe(finalize(() => setLoading((loading) => ({ ...loading, topUps: false }))))
			.subscribe(({ packages }) => {
				setTopUpPackages(packages);
			});
		return () => {
			subscription.unsubscribe();
		};
	}, []);

	const allowedPlanPackages: SinglePackage[] = useMemo(() => {
		return planPackages.filter((singlePackage: SinglePackage) =>
			isPackageAllowed(subscriberProducts, singlePackage)
		);
	}, [planPackages, subscriberProducts]);

	const allowedTopUpPackages: SinglePackage[] = useMemo(() => {
		return topUpPackages.filter((singlePackage: SinglePackage) =>
			hasRequiredProducts(subscriberProducts, singlePackage)
		);
	}, [topUpPackages, subscriberProducts]);

	const enabledPlanPackages: SinglePackage[] = useMemo(() => {
		return planPackages.filter((singlePackage: SinglePackage) => isEnabled(subscriberProducts, singlePackage));
	}, [planPackages, subscriberProducts]);

	const activePlanPackages: types.services.IPackage[] = useMemo(() => {
		return allowedPlanPackages
			.filter(
				(singlePackage: SinglePackage) => getStatus(subscriberProducts, singlePackage) === SERVICE_STATUS.ACTIVE
			)
			.map((singlePackage: SinglePackage) => {
				const subscriberProduct: SingleSubscriberProduct | undefined = subscriberProducts.find(
					({ productId }) => productId === getProductId(singlePackage.partNumber)
				);
				const serviceStatus = getStatus(subscriberProducts, singlePackage);
				const isPlanPackagePending = serviceStatus !== SERVICE_STATUS.ACTIVE;
				const date = subscriberProduct && isPlanPackagePending ? subscriberProduct.startDate : undefined;
				const actionType: types.services.actionType =
					serviceStatus === SERVICE_STATUS.ACTIVE ? 'TURN_OFF' : 'TURN_ON';
				const buttonHidden = (cannotBeTurnedOff || []).indexOf(singlePackage.partNumber) >= 0;
				let name = singlePackage.name;
				if (singlePackage.price && Number(singlePackage.price) > 0) {
					name += ` - ${translate('price', singlePackage.price)}`;
				}
				return {
					name: name,
					description: '',
					id: singlePackage.uniqueID,
					legacyProductId: singlePackage.partNumber,
					status: {
						type: serviceStatus,
						date,
					},
					place: singlePackage.place,
					buttons: buttonHidden
						? []
						: [
								{
									text:
										serviceStatus === SERVICE_STATUS.ACTIVE
											? translate('numbers.services.turn.off')
											: translate('numbers.services.turn.on'),
									color: serviceStatus === SERVICE_STATUS.ACTIVE ? 'tertiary-light' : 'primary-light',
									disabled:
										serviceStatus == SERVICE_STATUS.PENDING_ACTIVATION ||
										serviceStatus == SERVICE_STATUS.PENDING_TERMINATION ||
										serviceStatus == SERVICE_STATUS.PENDING,
									type: actionType,
									action: () => turnOff(singlePackage, actionType),
								},
						  ],
				};
			});
	}, [allowedPlanPackages, subscriberProducts, turnOff]);

	const activeTopUpPackages: types.services.IPackage[] = useMemo(() => {
		return topUpPackages
			.filter(
				(singlePackage: SinglePackage) =>
					isPackageAllowed(subscriberProducts, singlePackage) &&
					getStatus(subscriberProducts, singlePackage) === SERVICE_STATUS.ACTIVE
			)
			.map((singlePackage: SinglePackage) => {
				const selectedSubscriberProducts: SingleSubscriberProduct[] = subscriberProducts.filter(
					({ productId }) => productId === getProductId(singlePackage.partNumber)
				);
				const pendingSelectedPackages = selectedSubscriberProducts.filter(({ status }) => status !== 'Active');
				const isPlanPackagePending = !!pendingSelectedPackages.length;
				const statusType: types.services.statusType = isPlanPackagePending ? 'PENDING' : 'ACTIVE';
				const date = isPlanPackagePending ? pendingSelectedPackages[0].startDate : undefined;
				return {
					name: `${translate('number.services.top.up')} - ${singlePackage.name}`,
					description: '',
					id: singlePackage.uniqueID,
					legacyProductId: singlePackage.partNumber,
					status: {
						type: statusType,
						date,
					},
					place: singlePackage.place,
					buttons: [],
				};
			});
	}, [topUpPackages, subscriberProducts]);

	const availablePackages: types.services.IPackage[] = useMemo(() => {
		const isPlanPackagePending: boolean = activePlanPackages.some(
			(singlePlanPackage) => singlePlanPackage.status?.type === 'PENDING'
		);
		const isTopUpPackagePending: boolean = activeTopUpPackages.some(
			(singlePlanPackage) => singlePlanPackage.status?.type === 'PENDING'
		);
		const buttons: types.services.ISingleButton[] = [];
		if (allowedPlanPackages.length) {
			if (activePlanPackages.length) {
				buttons.push({
					text: translate('numbers.details.change-plan'),
					color: 'primary-light',
					type: 'TURN_ON',
					disabled: isPlanPackagePending,
					action: changePlan,
				});
			} else {
				buttons.push({
					text: translate('numbers.services.turn.on'),
					color: 'primary-light',
					type: 'TURN_ON',
					disabled: isPlanPackagePending,
					action: changePlan,
				});
			}
		}
		if (allowedTopUpPackages.length) {
			buttons.push({
				text: translate('number.services.top.up'),
				color: 'primary-light',
				type: 'TOP_UP',
				disabled: isTopUpPackagePending,
				action: activateTopUp,
			});
		}
		return [
			{
				name: translate('number.services.mobile.internet.plans'),
				description: translate('number.services.mobile.internet.plans.description'),
				id: 'MOBILE_INTERNET_PLANS',
				status: undefined,
				legacyProductId: '',
				place: '',
				buttons,
			},
			...activePlanPackages,
			...activeTopUpPackages,
		];
	}, [
		activePlanPackages,
		activeTopUpPackages,
		allowedTopUpPackages,
		allowedPlanPackages,
		activateTopUp,
		changePlan,
		language,
	]);

	const packages: SinglePackage[] = useMemo(() => {
		switch (dialogType) {
			case null:
				return [];
			case 'topUpDialog':
				return allowedTopUpPackages;
			default:
				return allowedPlanPackages;
		}
	}, [dialogType, allowedTopUpPackages, allowedPlanPackages]);

	const mobileInternetDialogHeader: string = useMemo(() => {
		switch (dialogType) {
			case 'changePlanDialog':
				return translate('services.number.gobiz.change.plan');
			case 'topUpDialog':
				return translate('number.services.top.up');
			default:
				return '';
		}
	}, [dialogType]);

	const buttonText: string = useMemo(() => {
		switch (dialogType) {
			case 'changePlanDialog':
				return translate('change');
			case 'topUpDialog':
				return translate('number.services.top.up');
			default:
				return '';
		}
	}, [dialogType]);

	useEffect(() => {
		setIsMobileInternetPlansActive(availablePackages.some((pckg) => pckg.status?.type === 'ACTIVE'));
	}, [availablePackages]);

	return (
		<ConditionalRender
			show={Object.values(loading).some((value) => value)}
			onTrue={() => <CustomCircularProgress text={translate('number.loading.packages')} loading />}
			onFalse={() => (
				<>
					<div className={classes.accordionContentWrapper}>
						<AccordionContent
							elements={availablePackages}
							note={translate('number.services.downgrade')}
							confirmations={[{ type: 'TURN_OFF' }]}
						/>
					</div>
					<ActivateMobileInternetDialog
						isOpen={dialogType !== null}
						packages={packages}
						enabledPackages={enabledPlanPackages}
						hideDialog={hideDialog}
						header={mobileInternetDialogHeader}
						buttonText={buttonText}
						additionalText={dialogType === 'changePlanDialog' ? translate('number.services.downgrade') : ''}
						type={dialogType === 'topUpDialog' ? 'TOP_UP' : 'PLAN'}
						termsAndConditions={{
							type: 'link',
							text: translate('number.services.mobile.internet.terms'),
							url: apiConfig.links.mobileInternet,
						}}
						updateServices={updateServices}
					/>
				</>
			)}
		/>
	);
};

export default MobileInternetPlansContentWrapper;
